import { Add } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
  NoteDTO,
  archiveInvoiceNote,
  createInvoiceNote,
  createNote,
  createPlacementNote,
  getNoteById,
  updateInvoiceNote,
  updateNote,
  updatePlacementNote,
  archivePlacementNote,
  archiveNote,
  unarchiveNote,
  getAllRecruiterNotes,
  getNotesByClientIdAndClientContactId,
  getAllNotes,
  getAllNotesByWorkspaceId,
  IFilters,
  ClientDTO,
  ClientContactDTO,
  getAllNotesTags,
  getTaskById,
  ITask,
  taskSearch,
} from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsListingLoader,
  WsPageHeader,
  WsToggleButton,
  formattedDateISOWithZeroTime,
} from 'wisestep-base';

import { NotesParent } from '../../../../components/communicationBar/Notes';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext';
import Resizable from '../../../../features/candidateLayout/components/childComponets/resizable/Resizable';
import NoPreview from '../../../../features/candidateLayout/components/documents/components/NoPreview';
import ConfirmationDialog from '../../../../features/clientDetails/components/ConfirmationDialog';
import NotesFilters from '../../../../features/notes/components/NotesFilters';
import NotesLeftPanel from '../../../../features/notes/components/NotesLeftPanel';
import NotesRightPanel from '../../../../features/notes/components/NotesRightPanel';
import {
  notesDefaultFilterValues,
  noteTypesNew,
  TaskDefaultQuery,
} from '../../../../features/notes/helper/NotesHelper';
import { useDialog } from '../../../../hooks/useDialog';
import getDecodedQueryParams from '../../../../utils/getDecodedQueryParams';
import { success, failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

import {
  ListingSearchPropsForNotesListing,
  notesAmplitudeEvents,
  setDefaultSearchFilterNotesListing,
} from './helper/notesHelper';
import NotesPagination from './NotesPagination';

interface IComposeNoteProps {
  parentId: string;
  parent: NotesParent;
  isClient?: boolean;
  clientContactId?: string;
  email?: string;
}

const ComposeNote = ({ parentId, parent, isClient, clientContactId, email }: IComposeNoteProps) => {
  const [searchParams] = useSearchParams();
  const queryData = searchParams.get('d') ?? '';
  const defaultValues = useMemo<IFilters>(
    () => (queryData ? getDecodedQueryParams(queryData) : notesDefaultFilterValues),
    [queryData]
  );
  const [note, setNote] = useState<NoteDTO>();
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const methods = useForm<NoteDTO>();
  const [notes, setNotes] = useState<NoteDTO[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, closeDialog] = useDialog();
  const [toggleParent, setToggleParent] = useState<string>('ACTIVE');
  const [filters, setFilters] = useState<IFilters>(defaultValues);
  const workspace = useWorkspaceContext();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [tags, setTags] = useState<string[]>([]);
  const [task, setTask] = useState<ITask>();

  const [defaultFilterValues] = useState<ListingSearchPropsForNotesListing>(
    setDefaultSearchFilterNotesListing(defaultValues) ?? {}
  );
  const [noteSelectType, setNoteSelectType] = useState<string>();

  const fetchNotes = useCallback(async () => {
    if (!isEmpty(filters.reminderStatus) && isEmpty(filters.taskIds)) {
      setNotes([]);
      setNote({ id: 'new' } as NoteDTO);
      setTask({});
      setTotalCount(0);
      return;
    }
    setIsLoading(true);
    setNotes([]);
    setNote({ id: 'new' } as NoteDTO);
    const payload: IFilters = {
      search: filters.search,
      fromDate: filters.fromDate && formattedDateISOWithZeroTime(filters.fromDate),
      toDate: filters.toDate && formattedDateISOWithZeroTime(filters.toDate),
      createdBy: filters.createdBy,
      clientIds: filters.clientIds,
      contactIds: filters.contactIds,
      tags: filters.tags ?? [],
      toggleParent: toggleParent,
      pageNo: page,
      taskIds: filters.taskIds,
      type: filters.type,
      searchAcross: filters.searchAcross,
      noteType: filters.noteType,
      clientNames: filters.clientNames,
      noteSelectType: noteSelectType,
    };
    let response;
    let filteredNotes;
    switch (parent) {
      case NotesParent.CLIENTCONTACT:
        response = await getNotesByClientIdAndClientContactId(
          parentId,
          clientContactId ?? '',
          toggleParent
        );
        break;
      case NotesParent.USERPROFILE:
        response = await getAllRecruiterNotes(email ?? '', toggleParent);
        break;
      case NotesParent.CLIENT:
        response = await getAllNotes(parentId, toggleParent);
        break;
      default:
        response = await getAllNotesByWorkspaceId(payload);
        if (response) {
          setTotalCount(response.data.totalElements);
        }
        break;
    }
    if (response) {
      filteredNotes = parent == NotesParent.NOTELISTING ? response?.data.content : response?.data;
    }

    if (filteredNotes?.length > 0) {
      setNotes(filteredNotes);
      setNote({ ...filteredNotes[0], tags: filteredNotes[0].tags ?? [] });
      const noteFirst = filteredNotes[0];
      if (noteFirst && noteFirst.taskId) {
        const taskRes = await getTaskById(noteFirst.taskId ?? '');
        setTask(taskRes.data);
      }
    }
    setIsLoading(false);
  }, [clientContactId, email, filters, parent, parentId, toggleParent, page, noteSelectType]);

  const handleFilterSubmit = async (formValues: IFilters) => {
    const search = formValues.query ?? formValues.search;
    setPage(0);
    setNotes([]);
    setNote({ id: 'new' } as NoteDTO);
    let taskIds: string[] = [];
    if (!isEmpty(formValues.reminderStatus))
      taskIds = await getAllTaskIds(formValues.reminderStatus ?? '');
    const clientIds = formValues?.clients?.map((client: ClientDTO) => client.id);
    const contactIds =
      formValues?.contacts
        ?.map((contact: ClientContactDTO) => contact.id)
        .filter((id): id is string => id !== null) ?? [];
    const updatedFormValues: IFilters = {
      ...formValues,
      clientIds: clientIds,
      contactIds: contactIds,
      taskIds: taskIds,
      search: search,
    };
    setFilters(updatedFormValues);
  };

  const getAllTaskIds = async (status: string) => {
    const query = {
      ...TaskDefaultQuery,
      status,
    };
    try {
      const response = await taskSearch(query);
      const tasks: ITask[] = response?.data ?? [];
      return tasks.map((task) => task.id!).filter((id) => id !== null);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return [];
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const editNote = () => {
    setIsEditing(true);
    notesAmplitudeEvents('note_edit', 'Edit Note', workspace);
    methods.reset(note);
  };

  useEffect(() => {
    if (isAdding) {
      setNotes((prevNotes) => [
        { heading: 'Untitled', description: 'Empty' } as NoteDTO,
        ...prevNotes,
      ]);
      setNote({ id: 'new' } as NoteDTO);
    }
  }, [isAdding]);

  const getNote = async (noteId) => {
    setTask({});
    if (!isAdding) {
      const note = await getNoteById(noteId);
      setNote({ ...note.data, tags: note.data.tags ?? [] });
      if (note.data && note.data?.taskId) {
        const taskRes = await getTaskById(note?.data.taskId ?? '');
        setTask(taskRes.data);
      }
    } else {
      setNote({ id: 'new' } as NoteDTO);
    }
  };

  const handleClickAddNote = () => {
    notesAmplitudeEvents('note_add', 'Add Note', workspace);
    methods.reset({ heading: '', description: '', noteType: '' });
    setIsAdding(true);
  };

  const handleCancelNote = () => {
    setIsEditing(false);
    setIsAdding(false);
    fetchNotes();
    methods.reset();
  };

  const handleArchiveNote = async (id: string) => {
    notesAmplitudeEvents('note_archived', 'Note Archived', workspace);
    try {
      if (parent === NotesParent.INVOICE) {
        await archiveInvoiceNote(id);
        setNotification(success(`Note has been archive successfully !!`));
      } else if (parent === NotesParent.PLACEMENT) {
        await archivePlacementNote(id);
        setNotification(success(`Note has been archive successfully !!`));
      } else {
        await archiveNote(id);
        setNotification(success(`Note has been archived successfully !!`));
      }
    } catch (e) {
      setNotification(failed('Something went wrong !!'));
    } finally {
      setNotes([]);
      setNote({ id: 'new' } as NoteDTO);
      fetchNotes();
    }
  };

  const handleUnarchiveNote = async (id: string) => {
    notesAmplitudeEvents('note_unarchived', 'Note Unarchived', workspace);
    try {
      await unarchiveNote(id);
      setNotification(success(`Note has been unarchived successfully !!`));
    } catch (e) {
      setNotification(failed('Something went wrong !!'));
    } finally {
      setNotes([]);
      setNote({ id: 'new' } as NoteDTO);
      fetchNotes();
    }
  };

  const handleClickArchiveNote = (note: NoteDTO) => {
    openDialog({
      children: (
        <ConfirmationDialog
          prompt="Are you sure you want to Archive the note?"
          confirmText="Archive note"
          skipText="Cancel"
          onConfirm={() => {
            closeDialog();
            handleArchiveNote(note.id);
          }}
          onSkip={closeDialog}
        />
      ),
    });
  };

  const handleConfirmUnarchive = (note) => {
    closeDialog();
    handleUnarchiveNote(note.id);
  };

  const handleClickUnarchiveNote = (note: NoteDTO) => {
    openDialog({
      children: (
        <ConfirmationDialog
          prompt="Are you sure you want to Unarchive the note?"
          confirmText="Unarchive note"
          skipText="Cancel"
          onConfirm={() => handleConfirmUnarchive(note)}
          onSkip={closeDialog}
        />
      ),
    });
  };

  const handleToggleArchiveNote = (value) => {
    setPage(0);
    setNotes([]);
    setNote({ id: 'new' } as NoteDTO);
    const newValue = value === 'ACTIVE' ? 'ACTIVE' : 'ARCHIVE';
    setToggleParent(newValue);
  };

  useEffect(() => {
    const fetchTags = async () => {
      const tagsRes = await getAllNotesTags();
      setTags(tagsRes.data);
    };
    fetchTags();
  }, []);

  const saveNote = async (data) => {
    methods.reset();
    setIsLoading(true);
    try {
      if (parent === NotesParent.INVOICE) {
        data.id
          ? await updateInvoiceNote(data)
          : await createInvoiceNote({ ...data, invoiceId: parentId } as NoteDTO);
        setNotification(success(`Note has been updated successfully !!`));
      } else if (parent === NotesParent.PLACEMENT) {
        data.id
          ? await updatePlacementNote(data)
          : await createPlacementNote({ ...data, closureId: parentId } as NoteDTO);
        setNotification(success(`Note has been updated successfully !!`));
      } else if (parent === NotesParent.CLIENTCONTACT) {
        data.id
          ? await updateNote(data)
          : await createNote({
              ...data,
              client: { id: parentId },
              markClientNotes: isClient,
              clientContact: { id: clientContactId },
            } as NoteDTO);
        setNotification(success(`Note has been updated successfully !!`));
      } else {
        data.id
          ? await updateNote(data)
          : await createNote({
              ...data,
              client: { id: parentId },
              markClientNotes: isClient,
            } as NoteDTO);
        setNotification(success(`Note has been updated successfully !!`));
      }
      methods.reset();
    } catch (e) {
      setNotification(failed('Something went wrong !!'));
      notesAmplitudeEvents('note_save_fail', 'Note Creation', workspace);
    } finally {
      notesAmplitudeEvents('note_save_success', 'Note Creation', workspace);
      isEditing
        ? notesAmplitudeEvents('note_edit_save', 'Note Edit', workspace)
        : notesAmplitudeEvents('note_save', 'Note Creation', workspace);
    }
    setIsAdding(false);
    setIsEditing(false);
    setIsLoading(false);
    fetchNotes();
  };

  const getNoteType = (noteType: string) => setNoteSelectType(noteType);

  return (
    <FormProvider {...methods}>
      {parent == NotesParent.NOTELISTING && (
        <Grid item xs={12} mb={2}>
          <WsPageHeader dropDownOptions={noteTypesNew} onDropdownClick={getNoteType} />
        </Grid>
      )}
      <Grid container justifyContent="space-between" mr={2}>
        {parent != NotesParent.NOTELISTING && (
          <Grid item>
            <WsToggleButton
              buttonGroupPadding={6}
              buttonsList={[
                { label: 'Active', value: 'ACTIVE' },
                { label: 'Archived', value: 'ARCHIVE' },
              ]}
              defaultValue={toggleParent ?? 'ACTIVE'}
              selectedIndexValue={handleToggleArchiveNote}
            />
          </Grid>
        )}
        <Grid item>
          {parent != NotesParent.NOTELISTING &&
            parent != NotesParent.USERPROFILE &&
            toggleParent == 'ACTIVE' && (
              <WsButton
                size="medium"
                onClick={handleClickAddNote}
                startIcon={<Add fontSize="small" />}
              >
                Add Note
              </WsButton>
            )}
        </Grid>
      </Grid>

      {parent == NotesParent.NOTELISTING && (
        <NotesFilters
          onSubmit={handleFilterSubmit}
          defaultSearchFiltersValues={defaultFilterValues}
          tags={tags}
          toggleParent={toggleParent}
          handleArchiveNote={handleToggleArchiveNote}
          filterValue={filters}
        />
      )}
      {isLoading ? (
        <Grid item xs={12}>
          <WsListingLoader />
        </Grid>
      ) : (
        <Resizable
          leftPanel={
            <Grid sx={{ background: WsColors.White }}>
              {parent == NotesParent.NOTELISTING && (
                <NotesPagination
                  totalCount={totalCount}
                  page={page}
                  setPage={setPage}
                  isTitle={true}
                />
              )}
              <NotesLeftPanel
                getNote={getNote}
                isLoading={isLoading}
                notes={notes}
                isAdding={isAdding}
              />
              {parent == NotesParent.NOTELISTING && (
                <NotesPagination
                  totalCount={totalCount}
                  page={page}
                  setPage={setPage}
                  isTitle={false}
                />
              )}
            </Grid>
          }
          rightPanel={
            note && note?.id ? (
              <NotesRightPanel
                note={note}
                parentId={parentId}
                isAdding={isAdding}
                saveNote={saveNote}
                editNote={editNote}
                isEditing={isEditing}
                onCancel={handleCancelNote}
                onArchive={() => handleClickArchiveNote(note)}
                onUnarchive={() => handleClickUnarchiveNote(note)}
                parent={parent}
                task={task}
                noteSelectType={noteSelectType}
              />
            ) : (
              <Grid sx={{ background: WsColors.White }}>
                <NoPreview text={'Note is not available'} />
              </Grid>
            )
          }
          defaultSizes={[25, 75]}
          minSize="20%"
          maxSize="80%"
        />
      )}
    </FormProvider>
  );
};
export default ComposeNote;
